import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ReviewCriteriaGroup {
  id: number;
  review_criteria_group_id: number;
  name: string;
  active: number; // API returns 1/0 instead of boolean
  created_at: string;
  updated_at: string;
  created_by_user?: string;
  updated_by_user?: string;
}

export interface ReviewCriteriaGroupsResponse {
  count: number;
  active_count: number;
  inactive_count: number;
  review_criteria_groups: ReviewCriteriaGroup[];
}

export interface ReviewCriteriaGroupDetailResponse {
  review_criteria_group: ReviewCriteriaGroup;
}

export interface ReviewCriteriaGroupCreateRequest {
  name: string;
  active: number;
}

export interface ReviewCriteriaGroupUpdateRequest {
  name?: string;
  active?: number;
}

export interface ReviewCriteriaGroupStatsResponse {
  summary: {
    total_groups: number;
    active_groups: number;
    inactive_groups: number;
    groups_with_reports: number;
  };
  most_used_groups: (ReviewCriteriaGroup & { report_count: number })[];
  recent_groups: ReviewCriteriaGroup[];
}

export interface ManuscriptValidationRequest {
  manuscript_id: string;
}

export interface ExistingSubmission {
  pra_report_id: string;
  submitted_at: string;
  status: string;
  user_id: string;
  review_criteria_group_id: number;
  review_criteria_group_name: string;
}

export interface ManuscriptValidationResponse {
  exists: boolean;
  message: string;
  manuscript_id: string;
  total_submissions: number;
  latest_submission_date?: string;
  latest_submission_status?: string;
  existing_submissions: ExistingSubmission[];
}

export interface PRAReportFile {
  id?: number;
  pra_file_id?: number;
  /** Detail/upload responses */
  original_filename?: string;
  /** User documents list API (`/documents/user/`) */
  file_name?: string;
  s3_link?: string;
  content_type?: string;
  file_size?: number;
  uploaded_at?: string;
}

export interface PRAReport {
   status_count?: {
    PASS: number;
    FAIL: number;
  };
  pra_report_id: string;
  pra_report_uuid?: string;
  client_id: string;
  document_id: string;
  /** Some list responses mirror manuscript id here. */
  submission_id?: string;
  review_criteria_group_id: number;
  publisher_id: number;
  user_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  files?: PRAReportFile[];
  /** Present on list/detail API when screening has run; empty means pass when status is completed. */
  hold_codes?: HoldCode[];
  /** From documents list API; preferred over parsing document_id for server column. */
  original_server?: string;
  recommended_server?: string;
  /** Some API responses use this exact key (capital R). */
  Recommended_server?: string;
  /** Extracted / resolved title when available from the API. */
  manuscript_title?: string;
  extracted_title?: string;
}

export interface DocumentUploadRequest {
  manuscript_id: string;
  review_criteria_group_id: number;
  publisher_id: number;
  user_id: string;
  primary_file: File;
  supplementary_files?: File[];
}

export interface DocumentUploadResponse {
  message: string;
  pra_report: PRAReport;
  files_uploaded: number;
}

export interface DocumentStatusResponse {
  pra_report_id: string;
  pra_report_uuid?: string;
  client_id: string;
  document_id: string;
  status: string;
  review_criteria_group_id: number;
  publisher_id: number;
  user_id: string;
  created_at: string;
  updated_at: string;
  Author_correspondence: string[];
  screening_decision: string;
  original_server: string;
  recommended_server: string;
  Recommended_server: string;
  hold_codes: string[];
  manuscript_title: string;
  files: PRAReportFile[];
}

export interface ScreeningResultRow {
  section?: string;
  outcome?: string;
  question?: string;
  answer?: string;
  [key: string]: unknown;
}

export interface HoldCode {
  question_code: string;
  hold_code: string;
}

export interface EmailCode {
  question_code: string;
  email_code: string;
}

export interface TrimmedResult {
  review_criteria_code: string;
  review_criteria: string;
  display_pages?: string;
  secondary_output: string;
  result: string;
  confidence: 'HIGH' | 'LOW';
  hold_codes?: HoldCode[];
  email_codes?: EmailCode[];
}

export interface ScreeningResultsResponse {
  pra_report_id?: string;
  review_criteria_group_name?: string;
  status?:string;
  results?: TrimmedResult[];
  result?: unknown;
  hold_codes?: HoldCode[];
  email_codes?: EmailCode[];
}

export interface TokenUsage {
  total_input_tokens: number;
  total_output_tokens: number;
  total_reasoning_tokens: number;
}

/** GET /documents/{id}/token-usage/ — primary/secondary/total breakdown */
export interface TokenUsageBucket {
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
}

export interface TokenUsageBreakdownResponse {
  pra_report_id: string;
  primary: TokenUsageBucket;
  secondary: TokenUsageBucket;
  total: TokenUsageBucket;
}

export type FeedbackVerdict = 'UP' | 'DOWN';

export interface PraFeedbackRequest {
  review_criteria_code: string;
  verdict: FeedbackVerdict;
  remark?: string | null;
}

export interface PraFeedbackResponse {
  message: string;
  feedback_id: number;
  pra_report_uuid: string;
  review_criteria_code: string;
  username: string;
  verdict: FeedbackVerdict;
  remark: string | null;
}

/** @deprecated Use TokenUsageBreakdownResponse for token-usage endpoint */
export interface TokenUsageResponse extends TokenUsage {
  pra_report_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  private http = inject(HttpClient);
  private readonly API_URL = `${environment.apiUrl}/api`;

  // Manuscript validation methods
  validateManuscriptId(manuscriptId: string): Observable<ManuscriptValidationResponse> {
    return this.http.post<ManuscriptValidationResponse>(`${this.API_URL}/manuscripts/validate/`, {
      manuscript_id: manuscriptId
    });
  }

  savePraFeedback(praReportUuid: string,request: PraFeedbackRequest): Observable<PraFeedbackResponse> {
    return this.http.post<PraFeedbackResponse>(`${this.API_URL}/documents/${praReportUuid}/pra-feedback/`,request);
  }

  getPraFeedback(praReportUuid: string): Observable<PraFeedbackGetResponse> {
    return this.http.get<PraFeedbackGetResponse>(`${this.API_URL}/documents/${praReportUuid}/pra-feedback/`);}

  // Review Criteria Groups API methods
  getReviewCriteriaGroups(params?: {
    active?: boolean;
    search?: string;
    ordering?: string;
  }): Observable<ReviewCriteriaGroupsResponse> {
    let url = `${this.API_URL}/review-criteria-groups/`;
    const queryParams = new URLSearchParams();

    if (params?.active !== undefined) {
      queryParams.append('active', params.active.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.ordering) {
      queryParams.append('ordering', params.ordering);
    }

    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }

    return this.http.get<ReviewCriteriaGroupsResponse>(url);
  }

  getActiveReviewCriteriaGroups(): Observable<ReviewCriteriaGroupsResponse> {
    return this.getReviewCriteriaGroups({ active: true });
  }

  getReviewCriteriaGroupDetail(id: number): Observable<ReviewCriteriaGroupDetailResponse> {
    return this.http.get<ReviewCriteriaGroupDetailResponse>(`${this.API_URL}/review-criteria-groups/${id}/`);
  }

  createReviewCriteriaGroup(data: ReviewCriteriaGroupCreateRequest): Observable<{
    message: string;
    review_criteria_group: ReviewCriteriaGroup;
  }> {
    return this.http.post<{
      message: string;
      review_criteria_group: ReviewCriteriaGroup;
    }>(`${this.API_URL}/review-criteria-groups/`, data);
  }

  updateReviewCriteriaGroup(id: number, data: ReviewCriteriaGroupUpdateRequest): Observable<{
    message: string;
    review_criteria_group: ReviewCriteriaGroup;
  }> {
    return this.http.put<{
      message: string;
      review_criteria_group: ReviewCriteriaGroup;
    }>(`${this.API_URL}/review-criteria-groups/${id}/`, data);
  }

  deleteReviewCriteriaGroup(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.API_URL}/review-criteria-groups/${id}/`);
  }

  getReviewCriteriaGroupsStats(): Observable<ReviewCriteriaGroupStatsResponse> {
    return this.http.get<ReviewCriteriaGroupStatsResponse>(`${this.API_URL}/review-criteria-groups/stats/`);
  }

  // Document management methods
  uploadDocument(request: DocumentUploadRequest): Observable<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('manuscript_id', request.manuscript_id);
    formData.append('review_criteria_group_id', request.review_criteria_group_id.toString());
    formData.append('publisher_id', request.publisher_id.toString());
    formData.append('user_id', request.user_id);
    formData.append('primary_file', request.primary_file);

    if (request.supplementary_files && request.supplementary_files.length > 0) {
      request.supplementary_files.forEach(file => {
        formData.append('supplementary_files', file);
      });
    }

    return this.http.post<DocumentUploadResponse>(`${this.API_URL}/documents/upload/`, formData);
  }

  getDocumentStatus(praReportId: string): Observable<DocumentStatusResponse> {
    return this.http.get<DocumentStatusResponse>(`${this.API_URL}/documents/${praReportId}/status/`);
  }

  getUserDocuments(): Observable<PRAReport[]> {
    return this.http.get<{ documents: PRAReport[], count: number }>(`${this.API_URL}/documents/user/`)
      .pipe(
        map(response => response.documents)
      );
  }

  deleteDocument(praReportId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.API_URL}/documents/${praReportId}/`);
  }

  retryDocument(praReportId: string): Observable<{ message: string, status: string }> {
    return this.http.post<{ message: string, status: string }>(`${this.API_URL}/documents/${praReportId}/retry/`, {});
  }

  downloadDocument(praReportId: string, fileId: number): Observable<Blob> {
    return this.http.get(`${this.API_URL}/documents/${praReportId}/files/${fileId}/download/`, {
      responseType: 'blob'
    });
  }

  getReportTree(praReportId: string): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/documents/${praReportId}/tree/`);
  }

  getScreeningNotes(praReportId: string): Observable<ScreeningResultsResponse> {
    return this.http.get<ScreeningResultsResponse>(
      `${this.API_URL}/documents/${praReportId}/results/?is_screening_notes=true`
    );
  }

  getGeneralResults(praReportId: string): Observable<ScreeningResultsResponse> {
    return this.http.get<ScreeningResultsResponse>(
      `${this.API_URL}/documents/${praReportId}/results/?is_screening_details=true`
    );
  }

  getGeneralResultsv2(praReportId: string): Observable<ScreeningResultsResponse> {
    return this.http.get<ScreeningResultsResponse>(
      `${this.API_URL}/v1/beta/documents/${praReportId}/results/?is_screening_details=true`
    );
  }

  getFileExtracts(praReportId: string): Observable<FileExtractsResponse> {
    return this.http.get<FileExtractsResponse>(`${this.API_URL}/documents/${praReportId}/file-extracts/`);
  }

  getTokenUsage(praReportId: string): Observable<TokenUsageBreakdownResponse> {
    return this.http.get<TokenUsageBreakdownResponse>(
      `${this.API_URL}/documents/${praReportId}/token-usage/`
    );
  }

  /** GET /documents/{id}/email-template/ — accumulated email codes and generated email body */
  getEmailTemplate(praReportId: string): Observable<EmailTemplateResponse> {
    return this.http.get<EmailTemplateResponse>(
      `${this.API_URL}/documents/${praReportId}/email-template/`
    );
  }
}

export interface EmailTemplateMetadata {
  start?: string;
  middle?: string;
  end?: string;
}

export interface EmailTemplateItem {
  emailCode: string;
  text: string;
}

export interface EmailTemplateResponse {
  PraReportUUID?: string;
  template_type?: string;
  /** Accumulated email codes (e.g. ["MISSING DATA/METHODS/RESULTS", "MISSING REFERENCES"]) */
  email_codes?: string[];
  metadata?: EmailTemplateMetadata;
  /** Email code descriptions for the generated email body (numbered points) */
  emailCodesDesc?: EmailTemplateItem[];
  /** Legacy / alternate field names */
  pra_report_id?: string;
  review_criteria_group_name?: string;
  email_templates?: EmailTemplateItem[];
  matched_email_codes?: string[];
  unmatched_email_codes?: string[];
  context?: Record<string, unknown>;
  accumulated_email_codes?: string[];
  generated_email?: string;
  [key: string]: unknown;
}

export interface FileExtract {
  pra_file_extract_id: string;
  extract_type: string;
  status: string;
  extract_text: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
}

export interface FileWithExtracts {
  pra_file_id: string;
  file_name: string;
  file_size: number;
  uploaded_at: string | null;
  created_at: string | null;
  extracts: FileExtract[];
}

export interface SourceDetail {
  source: string;
  details: string;
}

export interface FileExtractsResponse {
  pra_report_id: string;
  status: string;
  hold_codes: HoldCode[];
  token_usage: TokenUsage;
  manuscript_title: SourceDetail;
  corresponding_authors: SourceDetail;
  files: FileWithExtracts[];
}

export interface PraFeedbackGetResponse {
  feedback: PraFeedbackResponse[];
}